import useSWR from "swr";
import { CONFIG } from "@/config-global";
import axios, { fetcher } from "@/utils/axios";

import type {
  ClientPortalAuthDto,
  ClientPortalFileDto,
  ClientPortalMilestoneDto,
  ClientPortalAuthResponseDto,
} from "./types";

// ----------------------------------------------------------------------
const CLIENT_PORTAL_ENDPOINT = `${CONFIG.serverUrl}/api/client-portal`;

const ENDPOINTS = {
  AUTHENTICATE: "authenticate",
  GET_PROJECT_FILES: "get-project-files",
  DOWNLOAD_FILE: "download-file",
  GENERATE_SAS_URL: "generate-sas-url",
  GET_PROJECT_MILESTONES: "get-project-milestones",
  GET_WORKSPACE_SETTINGS: "get-workspace-settings",
  GET_CLIENT_PROJECT: "get-client-project",
};

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: true,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

export function useGetClientPortalMilestones(projectId: string) {
  const url = projectId
    ? [
        `${CLIENT_PORTAL_ENDPOINT}`,
        {
          params: {
            projectId,
            endpoint: ENDPOINTS.GET_PROJECT_MILESTONES,
          },
        },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    milestones: ClientPortalMilestoneDto[];
  }>(url, fetcher, swrOptions);

  return {
    milestones: data?.milestones || [],
    milestonesLoading: isLoading,
    milestonesError: error,
    milestonesValidating: isValidating,
    milestonesRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

export function useGetClientPortalFiles(projectId: string) {
  const url = projectId
    ? [
        `${CLIENT_PORTAL_ENDPOINT}`,
        {
          params: {
            projectId,
            endpoint: ENDPOINTS.GET_PROJECT_FILES,
          },
        },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    files: ClientPortalFileDto[];
  }>(url, fetcher, swrOptions);

  return {
    files: data?.files || [],
    filesLoading: isLoading,
    filesError: error,
    filesValidating: isValidating,
    filesRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

/**
 * Authenticate to the client portal
 */
export async function authenticateClientPortal(
  authData: ClientPortalAuthDto
): Promise<ClientPortalAuthResponseDto> {
  try {
    const params = { endpoint: ENDPOINTS.AUTHENTICATE };
    const response = await axios.post(CLIENT_PORTAL_ENDPOINT, authData, {
      params,
    });

    return response.data;
  } catch (error: any) {
    // Handle 401 Unauthorized specifically
    if (error.response?.status === 401) {
      return {
        authenticated: false,
        error: "Invalid password",
      };
    }

    console.error("Error authenticating to client portal:", error);
    return {
      authenticated: false,
      error: "Authentication failed",
    };
  }
}

// ----------------------------------------------------------------------

/**
 * Get project files from the client portal
 */
export async function getClientPortalFiles(
  projectId: string,
  password: string
): Promise<ClientPortalFileDto[]> {
  try {
    const params = {
      projectId,
      password,
      endpoint: ENDPOINTS.GET_PROJECT_FILES,
    };
    const response = await axios.get(CLIENT_PORTAL_ENDPOINT, {
      params,
    });

    return response.data.files || [];
  } catch (error) {
    console.error("Error getting client portal files:", error);
    throw error;
  }
}

// ----------------------------------------------------------------------

/**
 * Download a file from the client portal
 */
export async function downloadClientPortalFile(
  blobName: string,
  projectId: string,
  password: string
) {
  try {
    const params = {
      blobName,
      projectId,
      password,
      endpoint: ENDPOINTS.DOWNLOAD_FILE,
    };
    const response = await axios.get(CLIENT_PORTAL_ENDPOINT, {
      params,
    });

    return {
      file: response.data.file, // Base64 encoded file
      blobName: response.data.blobName,
    };
  } catch (error) {
    console.error("Error downloading client portal file:", error);
    throw error;
  }
}

// ----------------------------------------------------------------------

/**
 * Generate a SAS URL for a blob in the client portal
 */
export async function generateClientPortalSasUrl(
  blobName: string,
  projectId: string,
  expiryMinutes?: number,
  downloadFileName?: string
) {
  try {
    const params = {
      blobName,
      projectId,
      expiryMinutes,
      downloadFileName,
      endpoint: ENDPOINTS.GENERATE_SAS_URL,
    };
    const response = await axios.get(CLIENT_PORTAL_ENDPOINT, {
      params,
    });

    return response.data.sasUrl;
  } catch (error) {
    console.error("Error generating client portal SAS URL:", error);
    throw error;
  }
}

// ----------------------------------------------------------------------

/**
 * Hook to get workspace settings for client portal (no auth required)
 */
export function useGetClientPortalWorkspaceSettings(
  workspaceId: string | null
) {
  const url = workspaceId
    ? [
        `${CLIENT_PORTAL_ENDPOINT}`,
        {
          params: {
            workspaceId,
            endpoint: ENDPOINTS.GET_WORKSPACE_SETTINGS,
          },
        },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    workspace: {
      id: string;
      name: string;
      metadata: any;
    };
  }>(url, fetcher, swrOptions);

  return {
    workspace: data?.workspace,
    isLoading,
    error,
    isValidating,
    refresh: mutate,
  };
}

// ----------------------------------------------------------------------

/**
 * Hook to get client project for client portal (no auth required)
 */
export function useGetClientPortalClientProject(projectId: string | null) {
  const url = projectId
    ? [
        `${CLIENT_PORTAL_ENDPOINT}`,
        {
          params: {
            id: projectId,
            endpoint: ENDPOINTS.GET_CLIENT_PROJECT,
          },
        },
      ]
    : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<{
    project: {
      id: string;
      name: string;
      description?: string;
      status: string;
      progress: number;
      clientId: string;
      workspaceId: string;
      createdAt: string;
      updatedAt: string;
      startDate?: string;
      endDate?: string;
      client?: {
        id: string;
        name: string;
        company: string;
        email: string;
      };
    };
  }>(url, fetcher, swrOptions);

  return {
    project: data?.project,
    projectLoading: isLoading,
    projectError: error,
    projectValidating: isValidating,
    projectRefresh: mutate,
  };
}

// ----------------------------------------------------------------------

/**
 * Get workspace settings for client portal (no auth required)
 */
export async function getClientPortalWorkspaceSettings(workspaceId: string) {
  try {
    const params = {
      workspaceId,
      endpoint: ENDPOINTS.GET_WORKSPACE_SETTINGS,
    };
    const response = await axios.get(CLIENT_PORTAL_ENDPOINT, {
      params,
    });

    return response.data.workspace;
  } catch (error) {
    console.error("Error getting client portal workspace settings:", error);
    throw error;
  }
}

// ----------------------------------------------------------------------

/**
 * Get client project for client portal (no auth required)
 */
export async function getClientPortalClientProject(projectId: string) {
  try {
    const params = {
      id: projectId,
      endpoint: ENDPOINTS.GET_CLIENT_PROJECT,
    };
    const response = await axios.get(CLIENT_PORTAL_ENDPOINT, {
      params,
    });

    return response.data.project;
  } catch (error) {
    console.error("Error getting client portal client project:", error);
    throw error;
  }
}
